import React, { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Edit3, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import ReactMarkdown from 'react-markdown';
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as Sentry from '@sentry/react';

interface MarkdownEditorModalProps {
  initialMarkdown: string;
  title: string;
  experimentId?: string;
  onSave?: () => void;
}

const MarkdownEditorModal: React.FC<MarkdownEditorModalProps> = ({
  initialMarkdown,
  title,
  experimentId,
  onSave
}) => {
  const [markdown, setMarkdown] = useState(initialMarkdown);
  const [isOpen, setIsOpen] = useState(false);
  const [copyButtonText, setCopyButtonText] = useState("Copy Markdown");

  // Social media URL conversion with debouncing
  const convertSocialMediaUrls = useCallback((text: string) => {
    let convertedText = text;

    // Convert YouTube URLs
    const youtubeRegex = /(?<!\]\()\b(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})(?![^\s]*\))/g;
    convertedText = convertedText.replace(youtubeRegex, (match, videoId) => {
      const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/0.jpg`;
      const originalUrl = match.startsWith('http') ? match : `https://${match}`;
      return `[![YouTube Video](${thumbnailUrl})](${originalUrl})`;
    });

    // Convert Instagram reels and posts (only standalone URLs, not already in markdown links)
    const instagramRegex = /(?<!\]\([^\)]*)\b(?:https?:\/\/)?(?:www\.)?instagram\.com\/(reel|p)\/([a-zA-Z0-9_-]+)(?:\/[^\s]*)?(?![^\)]*\))/g;
    convertedText = convertedText.replace(instagramRegex, (match, type, postId) => {
      const thumbnailUrl = `https://www.instagram.com/p/${postId}/media?size=l`;
      const originalUrl = match.startsWith('http') ? match : `https://${match}`;
      return `[![Instagram ${type === 'reel' ? 'Reel' : 'Post'}](${thumbnailUrl})](${originalUrl})`;
    });

    return convertedText;
  }, []);

  const debouncedMarkdownUpdate = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (value: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          const convertedValue = convertSocialMediaUrls(value);
          if (convertedValue !== value) {
            setMarkdown(convertedValue);
          }
        }, 1000); // 1 second debounce
      };
    })(),
    [convertSocialMediaUrls]
  );

  const handleMarkdownChange = (value: string) => {
    setMarkdown(value);
    debouncedMarkdownUpdate(value);
  };

  const saveMutation = useMutation({
    mutationFn: async (markdownContent: string) => {
      if (!experimentId) {
        throw new Error("Experiment ID is required to save");
      }

      const { data: apiResponse, error } = await supabase.functions.invoke('tastermonial-api', {
        body: {
          method: 'PUT',
          endpoint: `/api/v2/experiments/${experimentId}`,
          body: {
            description_markdown: markdownContent
          }
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to save markdown');
      }

      if (apiResponse.status !== 200) {
        throw new Error(`API error: ${apiResponse.statusText || 'Failed to save markdown'}`);
      }

      return apiResponse.data;
    },
    onSuccess: () => {
      toast.success("Markdown description saved successfully");
      setIsOpen(false);
      onSave?.(); // Refresh the experiment query
    },
    onError: (error) => {
      toast.error(`Failed to save markdown: ${error.message}`);
              Sentry.captureException(error);
    },
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopyButtonText("Copied..");
      setTimeout(() => setCopyButtonText("Copy Markdown"), 2000);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const handleSave = () => {
    saveMutation.mutate(markdown);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 ml-2 opacity-60 hover:opacity-100"
        >
          <Edit3 className="h-3 w-3" />
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[90vw] max-w-none max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit {title}</DialogTitle>
          {experimentId ? (
            <p className="text-xs text-muted-foreground mt-1">
              Edit the markdown content and click Save to update the experiment.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              This editor is for preview and copy only. Changes here are not saved; copy the markdown and update it in the database.
            </p>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
            <div className="flex flex-col min-h-0">
              <h3 className="text-sm font-medium mb-2">Markdown Editor</h3>
              <ScrollArea className="flex-1">
                <Textarea
                  value={markdown}
                  onChange={(e) => handleMarkdownChange(e.target.value)}
                  className="min-h-[500px] font-mono text-sm resize-none border-none focus:ring-0 focus:ring-offset-0"
                  placeholder="Enter your markdown here..."
                />
              </ScrollArea>
            </div>
            <div className="flex flex-col min-h-0">
              <h3 className="text-sm font-medium mb-2">Preview</h3>
              <div className="flex-1 border rounded-md p-3 overflow-auto bg-gray-50">
                <div className="text-sm prose prose-sm max-w-none">
                  <ReactMarkdown
                    components={{
                      img: ({ src, alt }) => (
                        <img
                          src={src}
                          alt={alt}
                          className="w-full max-w-sm h-auto object-cover rounded-lg border my-2"
                        />
                      ),
                      h1: ({ children }) => (
                        <h1 className="text-xl font-bold mb-2">{children}</h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-lg font-semibold mb-2">{children}</h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-base font-medium mb-1">{children}</h3>
                      ),
                      p: ({ children }) => (
                        <p className="mb-2">{children}</p>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>
                      ),
                      li: ({ children }) => (
                        <li className="text-sm">{children}</li>
                      ),
                      a: ({ href, children }) => (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {children}
                        </a>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold">{children}</strong>
                      ),
                      em: ({ children }) => (
                        <em className="italic">{children}</em>
                      ),
                      code: ({ children }) => (
                        <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-gray-200 pl-4 italic my-2">{children}</blockquote>
                      ),
                    }}
                  >
                    {markdown || "Nothing to preview..."}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleCopy}>
            {copyButtonText}
          </Button>
          {experimentId && (
            <Button
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className="min-w-[80px]"
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          )}
          <Button onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MarkdownEditorModal;
